import { SearchOptionType, SelectedSearchOption } from '../helpers/types';
import { CHIP_TOGGLE_OPTIONS } from './constants';

interface Props {
	selectedSearchOptions: SelectedSearchOption[];
	searchTerm: string;
}

// Spotlight cross-page URL keys (matches paramount's filtersFromURL.ts).
export const NON_STICKY_TERM_PARAM = 'text';
export const STICKY_TERM_PARAM = 'singletext';
export const SEARCH_IN_URL_PARAM = 'searchIn'; // For chip toggles (e.g. title, description)

type ChipToggleKey = SearchOptionType.Title | SearchOptionType.Description;

const CHIP_TOGGLE_KEYS_BY_URL_TOKEN: Record<string, ChipToggleKey> = {
	title: SearchOptionType.Title,
	description: SearchOptionType.Description,
};

// Strips registry query field tokens, boolean operators and grouping characters -
// used to decode a stored query back to the user-typed term.
const DECODE_QUERY_REGEX = new RegExp(
	[...CHIP_TOGGLE_OPTIONS.map((d) => d.queryField), 'AND', 'OR', '[*()/]'].join('|'),
	'g',
);

export function parseSearchTermAndOptions(query: string): Props {
	if (!query?.trim()) {
		return { selectedSearchOptions: [], searchTerm: '' };
	}

	const selectedSearchOptions = CHIP_TOGGLE_OPTIONS.filter((searchOption) =>
		query.includes(searchOption.queryField),
	).map((searchOption) => searchOption.parseFromQuery(query));

	if (selectedSearchOptions.length === 0) {
		return { selectedSearchOptions: [], searchTerm: query };
	}

	const extractedSearchTerms = query
		.replaceAll(DECODE_QUERY_REGEX, ' ')
		.split(/\s+/)
		.filter(Boolean);

	const searchTerm = [...new Set(extractedSearchTerms)].join(' ');

	return { selectedSearchOptions, searchTerm };
}

// Parses `searchIn=title,description` from URL params into SelectedSearchOption[].
export const parseSearchOptionsFromURL = (urlParams: URLSearchParams): SelectedSearchOption[] => {
	const selectedOptions: SelectedSearchOption[] = [];

	const searchInParamValue = urlParams.get(SEARCH_IN_URL_PARAM);

	if (searchInParamValue) {
		searchInParamValue.split(',').forEach((urlToken) => {
			const chipOptionKey = CHIP_TOGGLE_KEYS_BY_URL_TOKEN[urlToken.trim().toLowerCase()];

			if (chipOptionKey) {
				selectedOptions.push({ key: chipOptionKey });
			}
		});
	}

	return selectedOptions;
};
